import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";

const styles = {
  tooltip: { "& .MuiTooltip-tooltip": { maxWidth: "320px" } },
  content: { display: "flex", flexDirection: "column", gap: "12px" },
  section: { display: "flex", flexDirection: "column", gap: "4px" },
  availableFrom: {
    borderTop: `1px solid ${theme.colors.neutral020}`,
    paddingTop: "12px",
  },
  button: {
    color: theme.colors.neutral040,
    padding: "4px",
    marginLeft: "4px",
    "@media (pointer: coarse)": { minWidth: "44px", minHeight: "44px" },
  },
  icon: { fontSize: "18px" },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ContactsInfoTooltipProps {
  dataAvailableFrom: string | null;
}

export const ContactsInfoTooltip: React.FC<ContactsInfoTooltipProps> =
  function ContactsInfoTooltip({ dataAvailableFrom }) {
    const { t } = useTranslation();
    const content: React.ReactNode = useMemo(
      () => (
        <Box sx={styles.content}>
          <Box sx={styles.section}>
            <Typography variant="body" fontWeight="medium">
              {t("billing.uniqueContacts.dailyValues", "Daily values")}
            </Typography>
            <Typography variant="body">
              {t("billing.uniqueContacts.tooltip")}
            </Typography>
          </Box>
          <Box sx={styles.section}>
            <Typography variant="body" fontWeight="medium">
              {t("billing.uniqueContacts.monthlyAverage", "Monthly average")}
            </Typography>
            <Typography variant="body">
              {t(
                "billing.uniqueContacts.monthlyAverageDescription",
                "Average of daily values for each month. This month uses available data only.",
              )}
            </Typography>
          </Box>
          {dataAvailableFrom !== null && (
            <Box sx={styles.availableFrom}>
              <Typography variant="note" color={theme.colors.staticFgBody}>
                {t("billing.uniqueContacts.dataAvailableFrom", {
                  defaultValue: "Data available from: {{date}}",
                  date: dataAvailableFrom.replaceAll("-", "/"),
                })}
              </Typography>
            </Box>
          )}
        </Box>
      ),
      [dataAvailableFrom, t],
    );

    return (
      <Tooltip
        title={content}
        arrow
        describeChild
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        sx={styles.tooltip}
      >
        <IconButton
          size="small"
          aria-label={t(
            "billing.uniqueContacts.infoLabel",
            "About active contacts",
          )}
          sx={styles.button}
        >
          <InfoOutlinedIcon sx={styles.icon} />
        </IconButton>
      </Tooltip>
    );
  };
